import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import axios from 'axios';
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    isWithinInterval,
    parseISO,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export interface CalendarEntry {
    id: string;
    employee: string | null;
    leave_type: string | null;
    start_date: string;
    end_date: string;
    is_own: boolean;
}

export interface CalendarHoliday {
    date: string;
    name: string;
}

interface VacationCalendarProps {
    initialEntries: CalendarEntry[];
    initialHolidays?: CalendarHoliday[];
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function VacationCalendar({ initialEntries, initialHolidays = [] }: VacationCalendarProps) {
    const { t } = useTranslations();
    const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
    const [entries, setEntries] = useState<CalendarEntry[]>(initialEntries);
    const [holidays, setHolidays] = useState<CalendarHoliday[]>(initialHolidays);
    const [loading, setLoading] = useState(false);

    // The initial month is provided by the server; fetch on subsequent navigation.
    const isInitialMonth = isSameMonth(month, startOfMonth(new Date()));

    useEffect(() => {
        if (isInitialMonth) {
            setEntries(initialEntries);
            setHolidays(initialHolidays);
            return;
        }

        let cancelled = false;
        setLoading(true);
        axios
            .get('/vacations/calendar-data', { params: { month: format(month, 'yyyy-MM') } })
            .then((res) => {
                if (!cancelled) {
                    setEntries(res.data.entries ?? []);
                    setHolidays(res.data.holidays ?? []);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [month, isInitialMonth, initialEntries, initialHolidays]);

    const holidayByDate = useMemo(() => new Map(holidays.map((h) => [h.date, h.name])), [holidays]);

    const days = useMemo(() => {
        const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
        const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
        return eachDayOfInterval({ start: gridStart, end: gridEnd });
    }, [month]);

    const entriesForDay = (day: Date): CalendarEntry[] =>
        entries.filter((entry) =>
            isWithinInterval(day, {
                start: parseISO(entry.start_date),
                end: parseISO(entry.end_date),
            }),
        );

    const today = new Date();

    return (
        <div className="rounded-lg border">
            <div className="flex items-center justify-between border-b p-3">
                <h3 className="text-base font-semibold">{t('vacations.calendar.title')}</h3>
                <div className="flex items-center gap-2">
                    <div className="text-muted-foreground flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1">
                            <span className="bg-info inline-block h-3 w-3 rounded-sm" />
                            {t('vacations.calendar.legend_you')}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="bg-success inline-block h-3 w-3 rounded-sm" />
                            {t('vacations.calendar.legend_others')}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="bg-warning inline-block h-3 w-3 rounded-sm" />
                            {t('vacations.calendar.legend_holiday')}
                        </span>
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        aria-label={t('vacations.calendar.previous')}
                        onClick={() => setMonth((m) => addMonths(m, -1))}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="w-32 text-center text-sm font-medium">{format(month, 'LLLL yyyy')}</span>
                    <Button variant="outline" size="icon" aria-label={t('vacations.calendar.next')} onClick={() => setMonth((m) => addMonths(m, 1))}>
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="text-muted-foreground grid grid-cols-7 border-b text-center text-xs font-medium">
                {WEEKDAYS.map((day) => (
                    <div key={day} className="p-2">
                        {day}
                    </div>
                ))}
            </div>

            <div className={`grid grid-cols-7 ${loading ? 'opacity-50' : ''}`}>
                {days.map((day) => {
                    const dayEntries = entriesForDay(day);
                    const inMonth = isSameMonth(day, month);
                    const isToday = isSameDay(day, today);
                    const holidayName = holidayByDate.get(format(day, 'yyyy-MM-dd'));

                    return (
                        <div
                            key={day.toISOString()}
                            className={`min-h-24 border-r border-b p-1 ${inMonth ? '' : 'bg-muted/30 text-muted-foreground'} ${
                                holidayName ? 'bg-warning-subtle' : ''
                            }`}
                        >
                            <div className={`mb-1 text-right text-xs ${isToday ? 'text-info font-bold' : ''}`}>{format(day, 'd')}</div>
                            <div className="space-y-1">
                                {holidayName && (
                                    <div
                                        title={holidayName}
                                        className="bg-warning text-warning-foreground truncate rounded px-1 py-0.5 text-[11px] font-medium"
                                    >
                                        {holidayName}
                                    </div>
                                )}
                                {dayEntries.slice(0, 3).map((entry) => (
                                    <div
                                        key={entry.id}
                                        title={`${entry.employee ?? ''}${entry.leave_type ? ` · ${entry.leave_type}` : ''}`}
                                        className={`truncate rounded px-1 py-0.5 text-[11px] ${entry.is_own ? 'bg-info text-info-foreground' : 'bg-success text-success-foreground'}`}
                                    >
                                        {entry.employee}
                                    </div>
                                ))}
                                {dayEntries.length > 3 && <div className="text-muted-foreground text-[10px]">+{dayEntries.length - 3}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <p className="text-muted-foreground p-2 text-center text-xs">{t('vacations.calendar.details_hidden')}</p>
        </div>
    );
}
